import React, { useEffect, useRef, useState } from "react";
import { IoMdNotifications } from "react-icons/io";
import { FiFileText, FiClock } from "react-icons/fi";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { useNotificationNavigate } from "@/hooks";
import { useCompanyStore } from "@/Store";
import S from "./styles.module.scss";

interface IExpiringDoc {
  id: number;
  name: string;
  status: "expired" | "expiring";
  daysLeft: number;
}

interface ITaskRisk {
  id: number;
  title: string;
  project: string | null;
}

interface INotificationItem {
  id: number;
  type: "info" | "success" | "warning" | "danger";
  title: string;
  message: string;
  link: string | null;
  companyId?: number;
  read: boolean;
  createdAt: string;
}

const Notification = () => {
  const { getRequest, patchRequest } = useApi();
  const openNotification = useNotificationNavigate();
  const companies = useCompanyStore((state) => state.companies);
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState<IExpiringDoc[]>([]);
  const [tasks, setTasks] = useState<ITaskRisk[]>([]);
  const [items, setItems] = useState<INotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const loadPersistent = () => {
    setError(false);
    getRequest("/api/notification?limit=10")
      .then((r) => r.json())
      .then((j) => setItems(j.items ?? []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    getRequest("/api/notification/unread-count")
      .then((r) => r.json())
      .then((j) => setUnread(j.response?.count ?? 0))
      .catch(() => {});
  };

  useEffect(() => {
    let active = true;
    getRequest("/api/document/expiring?days=30")
      .then((r) => r.json())
      .then((j) => {
        if (active) setDocs(j.response ?? []);
      })
      .catch(() => {});
    // Tarefas atrasadas vêm do dashboard (só admin); ignora 403 dos demais.
    getRequest("/api/dashboard/overview")
      .then((r) => r.json())
      .then((j) => {
        if (active) setTasks(j.response?.tasksAtRisk ?? []);
      })
      .catch(() => {});
    loadPersistent();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const handleItemClick = (item: INotificationItem) => {
    if (!item.read) {
      patchRequest(`/api/notification/${item.id}/read`, {}).catch(() => {});
      setItems((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, read: true } : n))
      );
      setUnread((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    openNotification(item);
  };

  const handleMarkAll = () => {
    patchRequest("/api/notification/read-all", {}).catch(() => {});
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const total = docs.length + tasks.length + unread;

  return (
    <div className={S.wrapper} ref={ref}>
      <button
        type="button"
        className={S["container-notification"]}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={
          total ? `Notificações: ${total} pendência(s)` : "Notificações"
        }
      >
        <IoMdNotifications size={24} color="#A6ABC8" />
        {total > 0 && <span>{total > 9 ? "+9" : total}</span>}
      </button>

      {open && (
        <div className={S.panel} aria-label="Notificações">
          <div className={S.panelHeader}>
            <span>Notificações</span>
            {unread > 0 && (
              <button
                type="button"
                className={S.markAllBtn}
                onClick={handleMarkAll}
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {total === 0 && !loading && !error && (
            <p className={S.empty}>Tudo em dia 🎉</p>
          )}

          <div className={S.section}>
            <span className={S.sectionTitle}>Recentes</span>
            {loading && <p className={S.stateMsg}>Carregando…</p>}
            {error && !loading && (
              <button
                type="button"
                className={S.retry}
                onClick={() => {
                  setLoading(true);
                  loadPersistent();
                }}
              >
                Não foi possível carregar. Tentar novamente.
              </button>
            )}
            {!loading && !error && items.length === 0 && (
              <p className={S.stateMsg}>Nenhuma notificação.</p>
            )}
            {!loading &&
              !error &&
              items.map((n) => {
                const companyName = companies.find(
                  (c) => c.id === n.companyId
                )?.name;
                return (
                  <button
                    type="button"
                    key={`n${n.id}`}
                    className={`${S.item} ${S.notifItem} ${n.read ? S.itemRead : ""}`}
                    onClick={() => handleItemClick(n)}
                  >
                    <span className={`${S.dot} ${S[n.type] ?? ""}`} />
                    <span className={S.notifBody}>
                      <span className={S.itemName} title={n.title}>
                        {n.title}
                      </span>
                      <span className={S.notifMessage} title={n.message}>
                        {n.message}
                      </span>
                      {companyName && (
                        <span className={S.notifCompany}>{companyName}</span>
                      )}
                    </span>
                    {!n.read && <span className={S.unreadMark} aria-hidden />}
                  </button>
                );
              })}
          </div>

          {docs.length > 0 && (
            <div className={S.section}>
              <span className={S.sectionTitle}>Documentos</span>
              {docs.slice(0, 5).map((d) => (
                <Link
                  key={`d${d.id}`}
                  to="/documentos"
                  className={S.item}
                  onClick={() => setOpen(false)}
                >
                  <FiFileText
                    size={15}
                    className={`${S.icon} ${
                      d.status === "expired" ? S.danger : S.warn
                    }`}
                  />
                  <span className={S.itemName} title={d.name}>
                    {d.name}
                  </span>
                  <span className={S.itemMeta}>
                    {d.status === "expired"
                      ? "vencido"
                      : d.daysLeft === 0
                        ? "vence hoje"
                        : `${d.daysLeft}d`}
                  </span>
                </Link>
              ))}
            </div>
          )}

          {tasks.length > 0 && (
            <div className={S.section}>
              <span className={S.sectionTitle}>Tarefas atrasadas</span>
              {tasks.slice(0, 5).map((t) => (
                <Link
                  key={`t${t.id}`}
                  to="/projetos"
                  className={S.item}
                  onClick={() => setOpen(false)}
                >
                  <FiClock size={15} className={`${S.icon} ${S.danger}`} />
                  <span className={S.itemName} title={t.title}>
                    {t.title}
                  </span>
                  <span className={S.itemMeta}>{t.project ?? "—"}</span>
                </Link>
              ))}
            </div>
          )}

          <Link
            to="/notificacoes"
            className={S.footerLink}
            onClick={() => setOpen(false)}
          >
            Ver todas
          </Link>
        </div>
      )}
    </div>
  );
};

export default Notification;

